import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useGetList, useListContext, useTranslate } from 'react-admin';
import { useChart } from 'src/components/chart';
import { fShortenNumber } from 'src/utils/format-number';
import { PredictionUserGrowthItem, predictionDashboardResources } from '../dataProvider';
import { PredictionChartCard } from './PredictionChartCard';
import {
  getPredictionLast30DayRange,
  formatPredictionTooltipDate,
  formatPredictionTooltipValue,
  formatPredictionXAxisLabel,
  PredictionChartPointContext,
} from './prediction-chart.helpers';

const getUserGrowthValue = (item: PredictionUserGrowthItem) =>
  Number(item.totalNewUsers ?? item.newUsers ?? item.users ?? 0);

export const PredictionUserGrowthChart = () => {
  const { filterValues } = useListContext();
  const translate = useTranslate();
  const theme = useTheme();

  const tradingType = filterValues?.tradingType;
  const { fromTime, toTime } = getPredictionLast30DayRange();
  const isPrediction = tradingType === 'Predict';
  const { data = [], isLoading: loading } = useGetList<PredictionUserGrowthItem>(
    predictionDashboardResources.userGrowthByDay,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'date', order: 'ASC' },
      filter: {
        fromTime,
        toTime,
      },
    },
    {
      enabled: isPrediction,
    }
  );

  const chart = useMemo(() => {
    const items = data ?? [];

    return {
      categories: items.map((item) => item.date),
      series: [
        {
          name: translate('module.dashBoard.newUsers', { _: 'New Users' }),
          type: 'bar' as const,
          data: items.map((item) => getUserGrowthValue(item)),
        },
      ],
    };
  }, [data, translate]);

  const chartOptions = useChart({
    chart: {
      height: 360,
    },
    plotOptions: {
      bar: {
        columnWidth: '42%',
        borderRadius: 4,
      },
    },
    colors: [theme.palette.success.main],
    legend: {
      show: true,
    },
    xaxis: {
      categories: chart.categories,
      labels: {
        rotate: -45,
        rotateAlways: true,
        formatter: formatPredictionXAxisLabel,
      },
    },
    yaxis: {
      title: {
        text: translate('module.dashBoard.newUsers', { _: 'New Users' }),
        style: { color: theme.palette.success.main },
      },
      labels: {
        formatter: (value: number) => fShortenNumber(value),
        style: { colors: theme.palette.success.main },
      },
    },
    tooltip: {
      x: {
        formatter: (value: string | number, context?: PredictionChartPointContext) =>
          formatPredictionTooltipDate(chart.categories, value, context),
      },
      y: {
        formatter: formatPredictionTooltipValue,
      },
    },
  });

  if (!isPrediction) return null;

  return (
    <PredictionChartCard
      title={translate('module.predictionDashboard.last30DaysUserGrowth', { _: 'Last 30 days user growth' })}
      subheader={translate('module.predictionDashboard.userGrowthSubheader', {
        _: 'Bar shows new users by day for the last 30 days',
      })}
      loading={loading}
      hasData={chart.categories.length > 0}
      chartType="bar"
      series={chart.series}
      options={chartOptions}
      emptyTitle={translate('common.noData', { _: 'No data' })}
    />
  );
};
